import {
    ArrowRightIcon,
    CheckIcon,
    ClockIcon,
    CopyIcon,
    Cross2Icon,
    ExternalLinkIcon,
    EyeOpenIcon,
    GlobeIcon,
    LightningBoltIcon,
    Pencil1Icon,
    RocketIcon,
    TransformIcon,
    UpdateIcon,
} from '@radix-ui/react-icons';
import React, { useMemo, useState } from 'react';
import { LuArrowLeft, LuChevronRight } from 'react-icons/lu';
import { Link, useParams } from 'react-router-dom';

import type { CmsPublicRoutingStatusView, CmsSecReviewView } from '@ndvr/cms-api';
import {
    CmsContentWorkflowStatus,
    CmsDraftPageContent,
    CmsMicrositePublicStatus,
    CmsMicrositeView,
    CmsPreviewView,
    CmsPublicRoutingStatus,
    CmsRenderArtifactStatus,
} from '@ndvr/cms-api';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useInternalUserDisplayName } from '@/modules/internal-user-queries';
import { cn } from '@/utils/cn';
import { copyText } from '@/utils/dom-utils';
import { MARKETING_CMS_SITES_PATH, marketingCmsSiteBuilderPath } from '@/views/marketing/marketing-routes';
import { Toast } from '@/widgets';

import { useCmsPermissions } from './cms-permissions';
import {
    cmsRenderArtifactShareSiteUrl,
    cmsRenderArtifactSiteUrl,
    useCmsContentVersionSecReview,
    useCmsDraftSecReview,
    useCmsHistory,
    useCmsHomepageContent,
    useCmsMicrosite,
    useCmsPublishStatus,
    useCmsVersionPreview,
    useCreateCmsRenderArtifactShareLink,
    useCreateCmsSecReviewForDraft,
    useCreateCmsSecReviewForVersion,
    usePublishCmsVersion,
    useSubmitCmsForReview,
} from './cms-queries';
import { CmsModuleListPreview, CmsSnapshotPreview } from './cms-rendering';
import { CmsRelativeDateTime } from './cms-ui';
import {
    canPublishVersion,
    CmsVersionRow,
    deriveCmsVersionRows,
    getActiveCmsCandidate,
    getPublishableVersionRows,
} from './cms-version-state';
import { CmsArtifactDiff } from './CmsArtifactDiff';
import { CmsSecAiReviewPanel } from './CmsSecAiReviewPanel';

const DRAFT_KEY = 'draft';

const initialsFor = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return '?';
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const isPendingRenderStatus = (status?: CmsRenderArtifactStatus): boolean =>
    status === CmsRenderArtifactStatus.REQUESTED || status === CmsRenderArtifactStatus.RENDERING;

const versionActivityLabel = (row: CmsVersionRow): string => {
    if (row.isPublished) {
        return 'Published';
    }
    switch (row.status) {
        case CmsContentWorkflowStatus.READY_FOR_REVIEW:
            return 'Submitted for review';
        case CmsContentWorkflowStatus.APPROVED:
            return 'Approved';
        case CmsContentWorkflowStatus.CHANGES_REQUESTED:
            return 'Changes requested';
        case CmsContentWorkflowStatus.PUBLISHED:
            return 'Published';
        default:
            return 'Saved';
    }
};

interface TimelineMarker {
    icon: React.ComponentType<{ className?: string }>;
    className: string;
}

const draftMarker: TimelineMarker = {
    icon: ClockIcon,
    className: 'border-espresso-200 bg-white text-espresso-500',
};

const versionMarker = (row: CmsVersionRow): TimelineMarker => {
    if (row.isPublished) {
        return { icon: RocketIcon, className: 'border-seafoam-300 bg-seafoam-0 text-seafoam-600' };
    }
    if (row.displayStatus === 'Publish failed') {
        return { icon: Cross2Icon, className: 'border-red-200 bg-red-0 text-red-400' };
    }
    switch (row.status) {
        case CmsContentWorkflowStatus.APPROVED:
            return { icon: CheckIcon, className: 'border-espresso-200 bg-white text-seafoam-600' };
        case CmsContentWorkflowStatus.CHANGES_REQUESTED:
            return { icon: UpdateIcon, className: 'border-espresso-200 bg-white text-gold-500' };
        case CmsContentWorkflowStatus.READY_FOR_REVIEW:
            return { icon: ClockIcon, className: 'border-espresso-200 bg-white text-espresso-500' };
        default:
            return { icon: CheckIcon, className: 'border-espresso-200 bg-white text-espresso-400' };
    }
};

/** Status the design surfaces in the header pill. */
const deriveSiteHeadlineStatus = (
    microsite: CmsMicrositeView | undefined,
    rows: CmsVersionRow[],
    draftUpdatedAt?: string,
): { label: string; tone: 'live' | 'pending' | 'draft' } => {
    const publishable = getActiveCmsCandidate(rows);
    const draftIsAheadOfLive =
        !!draftUpdatedAt &&
        !!microsite?.lastPublishedAt &&
        Date.parse(draftUpdatedAt) > Date.parse(microsite.lastPublishedAt);

    if (microsite?.publicStatus === CmsMicrositePublicStatus.PUBLISHED) {
        if (publishable || draftIsAheadOfLive) {
            return { label: 'Unpublished changes', tone: 'pending' };
        }
        return { label: 'Live', tone: 'live' };
    }

    if (microsite?.publicStatus === CmsMicrositePublicStatus.UNPUBLISHED) {
        return { label: 'Unpublished', tone: 'draft' };
    }

    return { label: 'Draft — not published yet', tone: 'draft' };
};

const statusDotClass = (tone: 'live' | 'pending' | 'draft'): string => {
    if (tone === 'live') {
        return 'bg-seafoam-500';
    }
    return tone === 'pending' ? 'bg-gold-300' : 'bg-espresso-300';
};

const previewLabelFor = (isDraftSelected: boolean, selectedRow?: CmsVersionRow): string => {
    if (isDraftSelected || !selectedRow) {
        return 'Current draft';
    }
    return selectedRow.isPublished
        ? `Version ${selectedRow.version.versionNumber} (Live)`
        : `Version ${selectedRow.version.versionNumber}`;
};

const StatTile = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="border-espresso-100 bg-white px-4 py-3">
        <p className="text-espresso-400 text-[11px] font-medium tracking-wide uppercase">{label}</p>
        <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
);

const publicRoutingLabel = (status?: CmsPublicRoutingStatus): string => {
    switch (status) {
        case CmsPublicRoutingStatus.ROUTED:
            return 'Verified';
        case CmsPublicRoutingStatus.NOT_ROUTED:
            return 'Not routed';
        case CmsPublicRoutingStatus.DISABLED:
            return 'Disabled';
        case CmsPublicRoutingStatus.MISSING:
            return 'Missing';
        case CmsPublicRoutingStatus.STALE:
            return 'Stale';
        case CmsPublicRoutingStatus.MISMATCH:
            return 'Mismatch';
        case CmsPublicRoutingStatus.UNREACHABLE:
            return 'Check failed';
        default:
            return 'Unknown';
    }
};

const publicRoutingVariant = (status?: CmsPublicRoutingStatus): React.ComponentProps<typeof Badge>['variant'] => {
    switch (status) {
        case CmsPublicRoutingStatus.ROUTED:
            return 'success';
        case CmsPublicRoutingStatus.MISSING:
        case CmsPublicRoutingStatus.STALE:
        case CmsPublicRoutingStatus.MISMATCH:
        case CmsPublicRoutingStatus.UNREACHABLE:
            return 'destructive';
        default:
            return 'secondary';
    }
};

const PublicRoutingStatusTile = ({
    status,
    isLoading,
    isError,
}: {
    status?: CmsPublicRoutingStatusView;
    isLoading: boolean;
    isError: boolean;
}) => {
    if (isLoading) {
        return <span className="text-espresso-500 text-sm font-medium">Checking...</span>;
    }
    if (isError || !status) {
        return <span className="text-destructive text-sm font-medium">Unavailable</span>;
    }
    return (
        <div className="space-y-1">
            <Badge variant={publicRoutingVariant(status.status)}>{publicRoutingLabel(status.status)}</Badge>
            <p className="text-espresso-500 text-xs leading-snug font-normal">{status.message}</p>
        </div>
    );
};

const BrowserChromePreview = ({ slug, children }: React.PropsWithChildren<{ slug: string }>) => (
    <div className="border-espresso-100 overflow-hidden rounded-md border bg-white">
        <div className="border-espresso-100 bg-espresso-50 flex items-center gap-2 border-b px-3 py-2">
            <span className="flex gap-1.5">
                <span className="bg-espresso-200 h-2.5 w-2.5 rounded-full" />
                <span className="bg-espresso-200 h-2.5 w-2.5 rounded-full" />
                <span className="bg-espresso-200 h-2.5 w-2.5 rounded-full" />
            </span>
            <span className="text-espresso-400 ml-1 flex min-w-0 items-center gap-1 rounded bg-white px-2 py-1 text-xs">
                <GlobeIcon className="h-3 w-3 shrink-0" />
                <span className="truncate">claroadvisors.com/advisors/{slug}</span>
            </span>
        </div>
        <div className="bg-white">
            <div className="origin-top overflow-hidden [&>div]:rounded-none [&>div]:border-0">{children}</div>
        </div>
    </div>
);

const PreviewPane = ({
    slug,
    draft,
    versionPreview,
    isVersionLoading,
}: {
    slug: string;
    draft?: CmsDraftPageContent;
    versionPreview?: CmsPreviewView;
    isVersionLoading: boolean;
}) => {
    if (versionPreview) {
        if (isPendingRenderStatus(versionPreview.renderArtifact?.status)) {
            return (
                <div className="border-espresso-100 bg-espresso-50 text-espresso-500 flex items-center justify-center gap-2 rounded-md border py-12 text-sm">
                    <UpdateIcon className="h-4 w-4 animate-spin" />
                    Rendering...
                </div>
            );
        }
        return (
            <BrowserChromePreview slug={slug}>
                <CmsSnapshotPreview preview={versionPreview} />
            </BrowserChromePreview>
        );
    }
    if (isVersionLoading) {
        return <p className="text-espresso-400 py-10 text-center text-sm">Loading preview…</p>;
    }
    if (draft) {
        return (
            <BrowserChromePreview slug={slug}>
                <CmsModuleListPreview draft={draft} />
            </BrowserChromePreview>
        );
    }
    return <p className="text-espresso-400 py-10 text-center text-sm">No draft content yet.</p>;
};

interface TimelineCardProps {
    title: string;
    badge?: { label: string; variant: 'success' | 'mint' | 'secondary' | 'outline' };
    timestamp?: string;
    authorName: string;
    activity: string;
    chip?: string;
    marker: TimelineMarker;
    isLast: boolean;
    isSelected: boolean;
    renderedSiteHref?: string;
    renderedSiteLabel?: string;
    renderedSitePending?: boolean;
    shareRenderedSite?: { onClick: () => void; disabled: boolean; pending: boolean };
    showPreview?: boolean;
    onPreview: () => void;
    onChanges: () => void;
    submitForReview?: { onClick: () => void; disabled: boolean; pending: boolean; disabledReason?: string };
    secReview?: { onClick: () => void; disabled: boolean; pending: boolean; disabledReason?: string };
    publish?: { onClick: () => void; disabled: boolean; disabledReason?: string; pending: boolean };
}

const TimelinePreviewActions = ({
    renderedSiteHref,
    renderedSiteLabel = 'Rendered',
    renderedSitePending,
    showPreview,
    onPreview,
    shareRenderedSite,
}: {
    renderedSiteHref?: string;
    renderedSiteLabel?: string;
    renderedSitePending?: boolean;
    shareRenderedSite?: { onClick: () => void; disabled: boolean; pending: boolean };
    showPreview: boolean;
    onPreview: () => void;
}) => {
    if (!showPreview) {
        return null;
    }

    return (
        <>
            <Button type="button" variant="ghost" size="sm" onClick={onPreview}>
                <EyeOpenIcon className="h-4 w-4" /> Preview
            </Button>
            {renderedSiteHref && !renderedSitePending && (
                <Button asChild variant="ghost" size="sm">
                    <a href={renderedSiteHref} target="_blank" rel="noopener noreferrer">
                        <ExternalLinkIcon className="h-4 w-4" /> {renderedSiteLabel}
                    </a>
                </Button>
            )}
            {!renderedSiteHref && renderedSitePending && (
                <Button type="button" variant="ghost" size="sm" disabled>
                    <UpdateIcon className="h-4 w-4 animate-spin" /> Rendering...
                </Button>
            )}
            {shareRenderedSite && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={shareRenderedSite.onClick}
                    disabled={shareRenderedSite.disabled || shareRenderedSite.pending}
                >
                    {shareRenderedSite.pending ? (
                        <UpdateIcon className="h-4 w-4 animate-spin" />
                    ) : (
                        <CopyIcon className="h-4 w-4" />
                    )}
                    Copy share link
                </Button>
            )}
        </>
    );
};

const TimelineCard = ({
    title,
    badge,
    timestamp,
    authorName,
    activity,
    chip,
    marker,
    isLast,
    isSelected,
    renderedSiteHref,
    renderedSiteLabel,
    renderedSitePending,
    shareRenderedSite,
    showPreview = true,
    onPreview,
    onChanges,
    submitForReview,
    secReview,
    publish,
}: TimelineCardProps) => {
    const MarkerIcon = marker.icon;
    return (
        <li className="relative flex gap-4 pb-4">
            {!isLast && <span className="bg-espresso-100 absolute top-9 bottom-0 left-[15px] w-px" aria-hidden />}
            <span
                className={cn(
                    'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border',
                    marker.className,
                )}
            >
                <MarkerIcon className="h-4 w-4" />
            </span>
            <div
                className={cn(
                    'border-espresso-100 min-w-0 flex-1 rounded-md border bg-white p-4 transition-shadow',
                    isSelected && 'ring-seafoam-300 ring-2',
                )}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold">{title}</h3>
                        {badge && (
                            <Badge variant={badge.variant} className="text-[10px] tracking-wide uppercase">
                                {badge.label}
                            </Badge>
                        )}
                    </div>
                    {timestamp && (
                        <span className="text-espresso-400 shrink-0 text-xs">
                            <CmsRelativeDateTime value={timestamp} />
                        </span>
                    )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                        <AvatarFallback className="bg-espresso-800 text-[10px] text-white">
                            {initialsFor(authorName)}
                        </AvatarFallback>
                    </Avatar>
                    <span className="text-espresso-600 text-sm">{authorName}</span>
                    <span className="text-espresso-300">·</span>
                    <span className="text-espresso-400 text-sm">{activity}</span>
                </div>
                {chip && (
                    <div className="mt-3">
                        <span className="bg-espresso-50 text-espresso-600 inline-flex max-w-full items-center rounded px-2 py-1 text-xs">
                            <span className="truncate">{chip}</span>
                        </span>
                    </div>
                )}
                <div
                    className="border-espresso-100 mt-3 flex items-center gap-1 overflow-x-auto border-t pt-3 whitespace-nowrap"
                    role="toolbar"
                    aria-label="Version actions"
                >
                    <TimelinePreviewActions
                        renderedSiteHref={renderedSiteHref}
                        renderedSiteLabel={renderedSiteLabel}
                        renderedSitePending={renderedSitePending}
                        shareRenderedSite={shareRenderedSite}
                        showPreview={showPreview}
                        onPreview={onPreview}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={onChanges}>
                        <TransformIcon className="h-4 w-4" /> Changes
                    </Button>
                    {submitForReview && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={submitForReview.onClick}
                            disabled={submitForReview.disabled || submitForReview.pending}
                            title={submitForReview.disabled ? submitForReview.disabledReason : undefined}
                        >
                            {submitForReview.pending ? (
                                <UpdateIcon className="h-4 w-4 animate-spin" />
                            ) : (
                                <RocketIcon className="h-4 w-4" />
                            )}
                            Submit for review
                        </Button>
                    )}
                    {secReview && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={secReview.onClick}
                            disabled={secReview.disabled || secReview.pending}
                            title={secReview.disabled ? secReview.disabledReason : undefined}
                        >
                            {secReview.pending ? (
                                <UpdateIcon className="h-4 w-4 animate-spin" />
                            ) : (
                                <LightningBoltIcon className="h-4 w-4" />
                            )}
                            AI SEC review
                        </Button>
                    )}
                    {publish && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={publish.onClick}
                            disabled={publish.disabled || publish.pending}
                            title={publish.disabled ? publish.disabledReason : undefined}
                        >
                            <RocketIcon className="h-4 w-4" /> Publish
                        </Button>
                    )}
                </div>
            </div>
        </li>
    );
};

export const CmsVersionHistory = () => {
    const { cmsMicrositeGUID = '' } = useParams();
    const permissions = useCmsPermissions();
    const micrositeQuery = useCmsMicrosite(cmsMicrositeGUID);
    const draftQuery = useCmsHomepageContent(cmsMicrositeGUID);
    const historyQuery = useCmsHistory(cmsMicrositeGUID);
    const publishStatusQuery = useCmsPublishStatus(cmsMicrositeGUID);
    const publishMutation = usePublishCmsVersion(cmsMicrositeGUID);
    const submitMutation = useSubmitCmsForReview(cmsMicrositeGUID);
    const createShareLinkMutation = useCreateCmsRenderArtifactShareLink();
    const createVersionSecReviewMutation = useCreateCmsSecReviewForVersion();
    const createDraftSecReviewMutation = useCreateCmsSecReviewForDraft();
    // submittedBy is an internal user; its GUID shares the UUID of the InternalUserProfile.
    const internalUserDisplayName = useInternalUserDisplayName();

    const microsite = micrositeQuery.data;
    const draft = draftQuery.data;
    const rows = useMemo(() => deriveCmsVersionRows(historyQuery.data, microsite), [historyQuery.data, microsite]);
    const publishableRows = getPublishableVersionRows(rows);
    const headlineStatus = deriveSiteHeadlineStatus(microsite, rows, draft?.updatedAt);
    const hasUnpublishedChanges = headlineStatus.tone === 'pending';
    const advisorName = microsite?.advisorDisplayName ?? 'Advisor';
    const canPublishMicrosite = permissions.canPublish;
    const canEditMicrosite = permissions.canEditMicrosite(microsite?.ownerUserGUID);
    const canRequestSecPreReview = permissions.canReview || canEditMicrosite;

    const [selectedKey, setSelectedKey] = useState<string>(DRAFT_KEY);
    const [diffVersionGUID, setDiffVersionGUID] = useState<string | undefined>();
    const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
    const [pendingSecReviewVersionGUID, setPendingSecReviewVersionGUID] = useState<string | undefined>();
    const [pendingDraftSecReview, setPendingDraftSecReview] = useState(false);
    const [pendingShareRenderArtifactGUID, setPendingShareRenderArtifactGUID] = useState<string | undefined>();
    const [secReviewDialog, setSecReviewDialog] = useState<{ review: CmsSecReviewView; label: string } | undefined>();
    const [reviewNotes, setReviewNotes] = useState('');

    const selectedRow = rows.find((row) => row.version.cmsContentVersionGUID === selectedKey);
    const selectedVersionGUID = selectedRow?.version.cmsContentVersionGUID;
    const isDraftSelected = selectedKey === DRAFT_KEY || !selectedRow;
    const selectedVersionPreview = useCmsVersionPreview(selectedRow?.version.cmsContentVersionGUID);
    const versionSecReviewQuery = useCmsContentVersionSecReview(isDraftSelected ? undefined : selectedVersionGUID);
    const draftSecReviewQuery = useCmsDraftSecReview(cmsMicrositeGUID, draft?.updatedAt);
    const diffPreview = useCmsVersionPreview(diffVersionGUID);
    const publishedPreview = useCmsVersionPreview(microsite?.publishedContentVersionGUID);
    const secReviewMutationPending = createVersionSecReviewMutation.isPending || createDraftSecReviewMutation.isPending;

    const latestWorkflowComment = (versionGUID: string): string | undefined => {
        const events = historyQuery.data?.workflowEvents.filter(
            (event) => event.cmsContentVersionGUID === versionGUID && event.comment?.trim(),
        );
        return events?.at(-1)?.comment ?? undefined;
    };

    const isLoading = micrositeQuery.isLoading || historyQuery.isLoading || draftQuery.isLoading;

    const publishVersion = (row: CmsVersionRow) =>
        publishMutation.mutate({ cmsContentVersionGUID: row.version.cmsContentVersionGUID });

    const requestVersionSecPreReview = async (row: CmsVersionRow) => {
        if (!canRequestSecPreReview) {
            return;
        }
        const versionGUID = row.version.cmsContentVersionGUID;
        setSelectedKey(versionGUID);
        setPendingSecReviewVersionGUID(versionGUID);
        try {
            const review = await createVersionSecReviewMutation.mutateAsync(versionGUID);
            if (review) {
                setSecReviewDialog({ review, label: `Version ${row.version.versionNumber}` });
            }
        } catch {
            // The mutation hook owns the error toast.
        } finally {
            setPendingSecReviewVersionGUID(undefined);
        }
    };

    const requestDraftSecPreReview = async () => {
        if (!canRequestSecPreReview) {
            return;
        }
        setSelectedKey(DRAFT_KEY);
        setPendingDraftSecReview(true);
        try {
            const review = await createDraftSecReviewMutation.mutateAsync(cmsMicrositeGUID);
            if (review) {
                setSecReviewDialog({ review, label: 'Working draft' });
            }
        } catch {
            // The mutation hook owns the error toast.
        } finally {
            setPendingDraftSecReview(false);
        }
    };

    const submitDraftForReview = async () => {
        if (!draft) {
            return;
        }
        await submitMutation.mutateAsync({ expectedDraftUpdatedAt: draft.updatedAt, comment: reviewNotes });
        setSubmitDialogOpen(false);
        setReviewNotes('');
    };

    const copyRenderedShareLink = async (cmsRenderArtifactGUID: string | undefined) => {
        if (!cmsRenderArtifactGUID) {
            return;
        }
        setPendingShareRenderArtifactGUID(cmsRenderArtifactGUID);
        try {
            const shareLink = await createShareLinkMutation.mutateAsync(cmsRenderArtifactGUID);
            const didCopy = await copyText(cmsRenderArtifactShareSiteUrl(shareLink.token));
            if (!didCopy) {
                throw new Error('Clipboard copy failed.');
            }
            Toast.success('Share link copied.');
        } catch (error) {
            Toast.forError(error, 'Failed to copy CMS share link.');
        } finally {
            setPendingShareRenderArtifactGUID(undefined);
        }
    };

    if (isLoading) {
        return <div className="text-espresso-500 p-8 text-sm">Loading version history…</div>;
    }

    if (!microsite) {
        return <div className="text-destructive p-8 text-sm">This microsite could not be loaded.</div>;
    }

    const previewLabel = previewLabelFor(isDraftSelected, selectedRow);
    const editorPath = marketingCmsSiteBuilderPath(cmsMicrositeGUID);
    const selectedSecReview = isDraftSelected ? draftSecReviewQuery.data : versionSecReviewQuery.data;
    const secReviewDisabledReason = !canRequestSecPreReview
        ? 'You need CMS review permission or site ownership to request AI SEC pre-review.'
        : secReviewMutationPending
          ? 'Another SEC pre-review request is already running.'
          : undefined;
    const requestSelectedSecPreReview = () => {
        if (isDraftSelected || !selectedRow) {
            return requestDraftSecPreReview();
        }

        return requestVersionSecPreReview(selectedRow);
    };

    return (
        <div className="px-6 py-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-3">
                        <Link
                            className="text-espresso-500 hover:text-espresso-700 focus:ring-ring inline-flex shrink-0 items-center gap-1.5 rounded-sm text-sm font-medium transition focus:ring-2 focus:outline-none"
                            to={MARKETING_CMS_SITES_PATH}
                        >
                            <LuArrowLeft className="size-4" />
                            Advisor Microsites
                        </Link>
                        <LuChevronRight className="text-espresso-300 size-4 shrink-0" />
                        <h1 className="font-heading truncate text-3xl leading-tight">
                            {advisorName} — {microsite.siteName}
                        </h1>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <span className={cn('h-2 w-2 rounded-full', statusDotClass(headlineStatus.tone))} />
                        <span className="text-espresso-500 text-xs font-medium tracking-wide uppercase">
                            {headlineStatus.label}
                        </span>
                    </div>
                    <a
                        href={`https://claroadvisors.com/advisors/${microsite.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-espresso-500 hover:text-espresso-700 mt-2 inline-flex items-center gap-1.5 text-sm"
                    >
                        <GlobeIcon className="h-4 w-4" />
                        <span>
                            claroadvisors.com/<span className="text-espresso-700">advisors/{microsite.slug}</span>
                        </span>
                        <ExternalLinkIcon className="h-3.5 w-3.5" />
                    </a>
                </div>
                {canEditMicrosite && (
                    <div className="flex flex-wrap items-center gap-2">
                        <Button asChild variant="highlighted">
                            <Link to={editorPath}>
                                <Pencil1Icon className="h-4 w-4" /> Edit draft
                            </Link>
                        </Button>
                    </div>
                )}
            </div>

            {/* Unpublished-changes banner */}
            {hasUnpublishedChanges && (
                <div className="border-gold-100 bg-gold-0 text-espresso-700 mt-5 rounded-md border px-4 py-3 text-sm">
                    This draft has edits that aren’t live yet. <span className="font-semibold">Publish</span> to push
                    them to your visitors, or keep working.
                </div>
            )}

            <div className="mt-6 grid gap-8 xl:grid-cols-2">
                {/* Left: preview + stats */}
                <div className="space-y-4">
                    <div className="border-espresso-100 rounded-md border bg-white p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-espresso-400 text-xs font-medium tracking-wide uppercase">
                                {previewLabel}
                            </p>
                            {isDraftSelected && canEditMicrosite && (
                                <Button asChild variant="link" size="sm" className="h-auto p-0">
                                    <Link to={editorPath}>
                                        Open in editor <ArrowRightIcon className="h-4 w-4" />
                                    </Link>
                                </Button>
                            )}
                        </div>
                        <div className="mt-3">
                            <PreviewPane
                                slug={microsite.slug}
                                draft={draft}
                                versionPreview={isDraftSelected ? undefined : selectedVersionPreview.data}
                                isVersionLoading={!isDraftSelected && selectedVersionPreview.isLoading}
                            />
                        </div>
                    </div>

                    <div className="border-espresso-100 bg-espresso-100 grid grid-cols-2 gap-px overflow-hidden rounded-md border">
                        <StatTile label="Status" value={headlineStatus.label} />
                        <StatTile label="Sections" value={draft?.modules.length ?? 0} />
                        <StatTile label="Total versions" value={rows.length} />
                        <StatTile
                            label="Public routing"
                            value={
                                <PublicRoutingStatusTile
                                    status={publishStatusQuery.data?.publicRouting}
                                    isLoading={publishStatusQuery.isLoading}
                                    isError={publishStatusQuery.isError}
                                />
                            }
                        />
                        <StatTile
                            label="Last published"
                            value={
                                microsite.lastPublishedAt ? (
                                    <span className="text-base font-medium">
                                        <CmsRelativeDateTime value={microsite.lastPublishedAt} />
                                    </span>
                                ) : (
                                    '—'
                                )
                            }
                        />
                    </div>

                    {(isDraftSelected ? draft : selectedRow) && (
                        <CmsSecAiReviewPanel
                            review={selectedSecReview}
                            isRequestPending={
                                isDraftSelected
                                    ? pendingDraftSecReview
                                    : pendingSecReviewVersionGUID === selectedVersionGUID
                            }
                            requestDisabledReason={secReviewDisabledReason}
                            onRequestReview={requestSelectedSecPreReview}
                        />
                    )}
                </div>

                {/* Right: version history timeline */}
                <div>
                    <div className="mb-4 flex items-center gap-3">
                        <p className="text-espresso-400 text-xs font-medium tracking-wide uppercase">Version history</p>
                        <span className="bg-espresso-100 h-px flex-1" />
                    </div>
                    <ul>
                        {/* Working draft entry */}
                        {draft && (
                            <TimelineCard
                                title="Working draft"
                                badge={{ label: 'Current draft', variant: 'mint' }}
                                timestamp={draft.updatedAt}
                                authorName={advisorName}
                                activity="Last edited"
                                chip={hasUnpublishedChanges ? 'Unpublished edits' : undefined}
                                marker={draftMarker}
                                isLast={rows.length === 0}
                                isSelected={isDraftSelected}
                                showPreview={false}
                                onPreview={() => setSelectedKey(DRAFT_KEY)}
                                onChanges={() => setDiffVersionGUID(microsite.publishedContentVersionGUID)}
                                submitForReview={
                                    canEditMicrosite
                                        ? {
                                              onClick: () => setSubmitDialogOpen(true),
                                              disabled: false,
                                              pending: submitMutation.isPending,
                                          }
                                        : undefined
                                }
                                secReview={{
                                    onClick: requestDraftSecPreReview,
                                    disabled: !canRequestSecPreReview || secReviewMutationPending,
                                    disabledReason: secReviewDisabledReason,
                                    pending: pendingDraftSecReview,
                                }}
                            />
                        )}
                        {rows.map((row, index) => {
                            const versionGUID = row.version.cmsContentVersionGUID;
                            const canPublish = canPublishVersion(row, canPublishMicrosite, publishableRows);
                            const selectedPreviewArtifact =
                                selectedKey === versionGUID ? selectedVersionPreview.data?.renderArtifact : undefined;
                            const renderStatus = selectedPreviewArtifact?.status ?? row.renderHealth;
                            const renderArtifactGUID =
                                selectedPreviewArtifact?.status === CmsRenderArtifactStatus.SUCCEEDED
                                    ? selectedPreviewArtifact.cmsRenderArtifactGUID
                                    : row.renderHealth === CmsRenderArtifactStatus.SUCCEEDED
                                      ? row.renderArtifactGUID
                                      : undefined;
                            return (
                                <TimelineCard
                                    key={versionGUID}
                                    title={`Version ${row.version.versionNumber}`}
                                    badge={
                                        row.isPublished
                                            ? { label: 'Live', variant: 'success' }
                                            : { label: row.displayStatus, variant: 'secondary' }
                                    }
                                    timestamp={row.latestActivityAt}
                                    authorName={internalUserDisplayName(row.version.submittedBy) || advisorName}
                                    activity={versionActivityLabel(row)}
                                    chip={latestWorkflowComment(versionGUID)}
                                    marker={versionMarker(row)}
                                    isLast={index === rows.length - 1}
                                    isSelected={selectedKey === versionGUID}
                                    renderedSiteHref={
                                        row.isPublished
                                            ? `https://claroadvisors.com/advisors/${microsite.slug}`
                                            : renderArtifactGUID
                                              ? cmsRenderArtifactSiteUrl(renderArtifactGUID)
                                              : undefined
                                    }
                                    renderedSiteLabel={row.isPublished ? 'Live Site' : undefined}
                                    renderedSitePending={row.isPublished ? false : isPendingRenderStatus(renderStatus)}
                                    shareRenderedSite={
                                        renderArtifactGUID
                                            ? {
                                                  onClick: () => copyRenderedShareLink(renderArtifactGUID),
                                                  disabled: isPendingRenderStatus(renderStatus),
                                                  pending: pendingShareRenderArtifactGUID === renderArtifactGUID,
                                              }
                                            : undefined
                                    }
                                    onPreview={() => setSelectedKey(versionGUID)}
                                    onChanges={() => setDiffVersionGUID(versionGUID)}
                                    secReview={{
                                        onClick: () => requestVersionSecPreReview(row),
                                        disabled: !canRequestSecPreReview || secReviewMutationPending,
                                        disabledReason: secReviewDisabledReason,
                                        pending: pendingSecReviewVersionGUID === versionGUID,
                                    }}
                                    publish={
                                        row.isPublished
                                            ? undefined
                                            : {
                                                  onClick: () => publishVersion(row),
                                                  disabled: !canPublish,
                                                  disabledReason: 'Only approved versions can be published.',
                                                  pending: publishMutation.isPending,
                                              }
                                    }
                                />
                            );
                        })}
                    </ul>
                    {rows.length === 0 && (
                        <p className="text-espresso-400 ml-12 text-sm">
                            No versions have been created yet. Edit the draft and submit it for review to create the
                            first version.
                        </p>
                    )}
                </div>
            </div>

            {/* Changes diff dialog */}
            <Dialog open={!!diffVersionGUID} onOpenChange={(open) => !open && setDiffVersionGUID(undefined)}>
                <DialogContent className="max-w-5xl">
                    <DialogHeader>
                        <DialogTitle>Changes vs. live</DialogTitle>
                    </DialogHeader>
                    {diffPreview.data ? (
                        <CmsArtifactDiff reviewPreview={diffPreview.data} publishedPreview={publishedPreview.data} />
                    ) : (
                        <p className="text-espresso-400 text-sm">Loading changes…</p>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={!!secReviewDialog} onOpenChange={(open) => !open && setSecReviewDialog(undefined)}>
                <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>SEC pre-review result</DialogTitle>
                        <DialogDescription>
                            {secReviewDialog?.label} pre-review results. This is not an approval.
                        </DialogDescription>
                    </DialogHeader>
                    {secReviewDialog && (
                        <CmsSecAiReviewPanel review={secReviewDialog.review} showRequestAction={false} />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={submitDialogOpen}
                onOpenChange={(open) => {
                    if (!submitMutation.isPending) {
                        setSubmitDialogOpen(open);
                    }
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Submit for review</DialogTitle>
                        <DialogDescription>
                            Add optional notes for the page content reviewer before submitting this draft.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="cmsHistoryReviewNotes">Reviewer notes</Label>
                        <Textarea
                            id="cmsHistoryReviewNotes"
                            rows={5}
                            value={reviewNotes}
                            disabled={submitMutation.isPending}
                            placeholder="Optional context, questions, or areas to review."
                            onChange={(event) => setReviewNotes(event.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={submitMutation.isPending}
                            onClick={() => setSubmitDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button type="button" disabled={submitMutation.isPending} onClick={submitDraftForReview}>
                            Submit for review
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
